/**
 * GPU particle shaders.
 *
 * Position data is intentionally treated as a calm base field. All breathing,
 * fluid drift, perspective sizing and twinkle happen per vertex on the GPU.
 */
export const particleVertexShader = /* glsl */ `
  uniform float uTime;
  uniform float uPixelRatio;
  uniform float uPointSize;
  uniform float uTurbulence;
  uniform float uBreath;
  uniform float uMotionScale;
  uniform float uMorphProgress;
  uniform float uScatterPower;
  uniform float uExpansion;
  uniform float uAttraction;
  uniform float uReleaseImpulse;
  uniform vec3 uAttractor;
  uniform vec3 uColorA;
  uniform vec3 uColorB;

  attribute vec3 aTarget;
  attribute vec4 aRandom;
  attribute float aSize;
  attribute float aTargetSize;
  attribute float aColorMix;
  attribute float aTargetColorMix;

  varying vec3 vColor;
  varying float vAlpha;
  varying float vFogDepth;
  varying float vSpark;
  varying float vTwinkle;

  float hash31(vec3 position) {
    position = fract(position * 0.1031);
    position += dot(position, position.yzx + 33.33);
    return fract((position.x + position.y) * position.z);
  }

  float noise3(vec3 position) {
    vec3 cell = floor(position);
    vec3 local = fract(position);
    local = local * local * (3.0 - 2.0 * local);

    float n000 = hash31(cell + vec3(0.0, 0.0, 0.0));
    float n100 = hash31(cell + vec3(1.0, 0.0, 0.0));
    float n010 = hash31(cell + vec3(0.0, 1.0, 0.0));
    float n110 = hash31(cell + vec3(1.0, 1.0, 0.0));
    float n001 = hash31(cell + vec3(0.0, 0.0, 1.0));
    float n101 = hash31(cell + vec3(1.0, 0.0, 1.0));
    float n011 = hash31(cell + vec3(0.0, 1.0, 1.0));
    float n111 = hash31(cell + vec3(1.0, 1.0, 1.0));

    float nx00 = mix(n000, n100, local.x);
    float nx10 = mix(n010, n110, local.x);
    float nx01 = mix(n001, n101, local.x);
    float nx11 = mix(n011, n111, local.x);
    float nxy0 = mix(nx00, nx10, local.y);
    float nxy1 = mix(nx01, nx11, local.y);
    return mix(nxy0, nxy1, local.z);
  }

  void main() {
    const float TAU = 6.28318530718;
    const float PI = 3.14159265359;
    float morphProgress = clamp(uMorphProgress, 0.0, 1.0);
    float arrival = smoothstep(0.48, 1.0, morphProgress);
    float scatterEnvelope = sin(morphProgress * PI);
    vec3 scatterDirection = normalize(aRandom.xyz * 2.0 - 1.0 + vec3(0.001));
    vec3 radialDirection = normalize(
      mix(position, aTarget, 0.16) + scatterDirection * 0.18 + vec3(0.001)
    );
    vec3 basePosition = mix(position, aTarget, arrival);
    basePosition +=
      (scatterDirection * 0.72 + radialDirection * 0.55) *
      scatterEnvelope *
      uScatterPower *
      (0.48 + aRandom.w * 0.75);

    vec3 morphTangent = normalize(vec3(
      -basePosition.y,
      basePosition.x,
      0.2 + aRandom.z * 0.3
    ) + vec3(0.001));
    basePosition +=
      morphTangent *
      sin(morphProgress * PI * 2.0 + aRandom.x * TAU) *
      scatterEnvelope *
      0.18;
    basePosition *= mix(0.72, 1.36, uExpansion);

    float breathPhase = uTime * 0.72 + aRandom.w * TAU;
    float breath = 1.0 + sin(breathPhase) * 0.018 * uBreath * uMotionScale;
    vec3 animatedPosition = basePosition * breath;

    vec3 noisePosition = basePosition * 0.72 + vec3(
      uTime * 0.045,
      -uTime * 0.032,
      uTime * 0.026
    ) * uMotionScale;
    float field = noise3(noisePosition + aRandom.xyz * 2.4) - 0.5;

    vec3 flow = normalize(vec3(
      sin(basePosition.y * 1.7 + uTime * 0.31 + aRandom.x * TAU),
      cos(basePosition.x * 1.35 - uTime * 0.24 + aRandom.y * TAU),
      sin((basePosition.x + basePosition.y) * 1.1 + aRandom.z * TAU)
    ));
    animatedPosition +=
      flow *
      field *
      uTurbulence *
      (1.0 + scatterEnvelope * 1.45) *
      0.26 *
      uMotionScale;

    vec3 tangent = normalize(vec3(
      -basePosition.y,
      basePosition.x,
      0.18 + sin(aRandom.x * TAU) * 0.12
    ));
    float orbitalDrift = sin(uTime * 0.28 + aRandom.y * TAU);
    animatedPosition += tangent * orbitalDrift * (0.018 + aRandom.z * 0.035) * uMotionScale;

    vec3 attractorCloud =
      uAttractor + scatterDirection * (0.045 + aRandom.y * 0.14);
    float attractionStrength =
      uAttraction * (0.78 + aRandom.w * 0.18);
    animatedPosition = mix(
      animatedPosition,
      attractorCloud,
      clamp(attractionStrength, 0.0, 0.96)
    );

    vec3 releaseDirection = normalize(
      animatedPosition - uAttractor + scatterDirection * 0.08 + vec3(0.001)
    );
    animatedPosition +=
      releaseDirection *
      uReleaseImpulse *
      (0.58 + aRandom.z * 1.35);

    vec4 modelPosition = modelMatrix * vec4(animatedPosition, 1.0);
    vec4 viewPosition = viewMatrix * modelPosition;
    gl_Position = projectionMatrix * viewPosition;

    float depthScale = 15.0 / max(1.0, -viewPosition.z);
    float twinkle = 0.82 + 0.18 * sin(
      uTime * (0.8 + aRandom.z * 1.8) + aRandom.x * 30.0
    ) * uMotionScale;
    float particleSize = mix(aSize, aTargetSize, arrival);
    gl_PointSize = clamp(
      uPointSize * particleSize * uPixelRatio * depthScale * twinkle,
      1.0,
      48.0
    );

    float radialMix = smoothstep(0.2, 3.1, length(basePosition));
    float colorMix = mix(aColorMix, aTargetColorMix, arrival);
    vColor = mix(uColorA, uColorB, clamp(colorMix * 0.78 + radialMix * 0.22, 0.0, 1.0));
    vAlpha = mix(0.48, 1.0, aRandom.w) * smoothstep(15.0, 4.0, -viewPosition.z);
    vFogDepth = -viewPosition.z;
    vSpark = step(0.91, aRandom.w);
    vTwinkle = twinkle;
  }
`;

export const particleFragmentShader = /* glsl */ `
  uniform float uOpacity;
  uniform float uGlow;
  uniform float uFogDensity;
  uniform vec3 uFogColor;

  varying vec3 vColor;
  varying float vAlpha;
  varying float vFogDepth;
  varying float vSpark;
  varying float vTwinkle;

  void main() {
    vec2 point = gl_PointCoord - 0.5;
    float radius = length(point);

    if (radius > 0.5) {
      discard;
    }

    float core = exp(-radius * radius * 145.0);
    float halo = exp(-radius * radius * 18.0) * 0.34;
    float horizontalRay = exp(-abs(point.y) * 48.0) * exp(-abs(point.x) * 5.0);
    float verticalRay = exp(-abs(point.x) * 48.0) * exp(-abs(point.y) * 5.0);
    float starRay = (horizontalRay + verticalRay) * 0.18 * vSpark;
    float softEdge = smoothstep(0.5, 0.34, radius);

    float energy = (core + halo + starRay) * softEdge;
    float alpha = energy * vAlpha * uOpacity;

    float fogFactor = 1.0 - exp(
      -uFogDensity * uFogDensity * vFogDepth * vFogDepth
    );
    alpha *= 1.0 - fogFactor * 0.78;

    vec3 energyColor = vColor * (
      0.74 + core * 1.85 + starRay * 1.5
    ) * uGlow * vTwinkle;
    energyColor = mix(energyColor, uFogColor, fogFactor * 0.18);

    gl_FragColor = vec4(energyColor, alpha);
  }
`;

/**
 * A deliberately sparse, shader-rendered background field. Its depth-scaled
 * sparks provide parallax and atmosphere without competing with the model.
 */
export const ambientVertexShader = /* glsl */ `
  uniform float uTime;
  uniform float uPixelRatio;
  uniform vec3 uColorA;
  uniform vec3 uColorB;

  attribute vec4 aRandom;

  varying vec3 vColor;
  varying float vEnergy;
  varying float vSpark;

  void main() {
    const float TAU = 6.28318530718;
    vec3 drift = vec3(
      sin(uTime * 0.035 + aRandom.x * TAU),
      cos(uTime * 0.028 + aRandom.y * TAU),
      sin(uTime * 0.021 + aRandom.z * TAU)
    );
    vec3 animatedPosition = position + drift * (0.025 + aRandom.w * 0.07);
    vec4 viewPosition = modelViewMatrix * vec4(animatedPosition, 1.0);
    gl_Position = projectionMatrix * viewPosition;

    float pulse = 0.72 + 0.28 * sin(
      uTime * (0.32 + aRandom.z * 0.7) + aRandom.x * 24.0
    );
    float depthScale = 12.0 / max(4.0, -viewPosition.z);
    gl_PointSize = clamp(
      (0.7 + aRandom.w * 2.4) * uPixelRatio * depthScale * pulse,
      1.0,
      8.0
    );

    vColor = mix(uColorA, uColorB, aRandom.y);
    vEnergy = mix(0.16, 0.72, aRandom.w) * pulse;
    vSpark = step(0.965, aRandom.w);
  }
`;

export const ambientFragmentShader = /* glsl */ `
  uniform float uOpacity;

  varying vec3 vColor;
  varying float vEnergy;
  varying float vSpark;

  void main() {
    vec2 point = gl_PointCoord - 0.5;
    float radius = length(point);
    if (radius > 0.5) {
      discard;
    }

    float core = exp(-radius * radius * 92.0);
    float halo = exp(-radius * radius * 14.0) * 0.26;
    float rayX = exp(-abs(point.y) * 62.0) * exp(-abs(point.x) * 6.0);
    float rayY = exp(-abs(point.x) * 62.0) * exp(-abs(point.y) * 6.0);
    float rays = (rayX + rayY) * 0.16 * vSpark;
    float edge = smoothstep(0.5, 0.3, radius);
    float energy = (core + halo + rays) * edge * vEnergy;

    gl_FragColor = vec4(vColor * (0.5 + core * 1.4), energy * uOpacity);
  }
`;
